"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldCheck, ArrowRight, FileText, CircleCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardBadge: string;
  h1: string;
  lead: string;
  relatedCta: string;
  chip: string;
  cardNote: string;
  cardLink: string;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Filter Mesh Specifications",
    standardBadge: "ISO 9044 / ASTM E2016",
    h1: "Precision Filter Mesh Specifications for Industrial Systems",
    lead: "Configure stainless steel wire mesh with verified aperture consistency, predictable pressure drop characteristics, and verified metallurgy for critical separation lines. Manufactured strictly to ISO 9044 and ASTM E2016 dimensional tolerances.",
    relatedCta: "Ordering specification guide",
    chip: "Plain & Dutch Weaves",
    cardNote: "Rolls, slit coils, stamped discs & cylinders",
    cardLink: "Supply formats"
  },
  zh: {
    categoryBadge: "过滤丝网技术规格",
    standardBadge: "ISO 9044 / ASTM E2016",
    h1: "面向工业系统的精密过滤丝网技术规格",
    lead: "针对严苛固液分离与气体过滤工段，系统配置具备高精度孔径一致性、可预测压差及合规合金材质的不锈钢编织丝网。生产制造全面遵循 ISO 9044 与 ASTM E2016 工业筛网公差标准。",
    relatedCta: "采购规格指南",
    chip: "平纹与密纹编织选项",
    cardNote: "原卷、分条卷带、冲压滤片与焊接滤筒",
    cardLink: "供货形式"
  },
  es: {
    categoryBadge: "Especificaciones de malla de filtración",
    standardBadge: "ISO 9044 / ASTM E2016",
    h1: "Especificaciones de malla de filtración para sistemas industriales",
    lead: "Configure telas metálicas de acero inoxidable con apertura uniforme comprobada, comportamiento de presión predecible y metalurgia verificada para procesos críticos. Fabricación conforme a las tolerancias ISO 9044 y ASTM E2016.",
    relatedCta: "Guía de especificación para pedidos",
    chip: "Tejidos liso y Dutch",
    cardNote: "Rollos, bobinas flejadas, discos estampados y cilindros",
    cardLink: "Formatos de suministro"
  },
  de: {
    categoryBadge: "Filtergewebe-Spezifikationen",
    standardBadge: "ISO 9044 / ASTM E2016",
    h1: "Präzisions-Filtergewebe-Spezifikationen für Industriesysteme",
    lead: "Konfigurieren Sie Edelstahldrahtgewebe mit nachgewiesener Maschenweitenkonsistenz, berechenbarem Differenzdruck und verifizierter Metallurgie für kritische Trennprozesse. Gefertigt nach Maßtoleranzen der Normen ISO 9044 und ASTM E2016.",
    relatedCta: "Bestell-Spezifikationsleitfaden",
    chip: "Leinwand- & Tressengewebe",
    cardNote: "Rollen, Spaltbänder, Stanzronden & Filterzylinder",
    cardLink: "Lieferformen"
  },
  fr: {
    categoryBadge: "Spécifications des toiles de filtration",
    standardBadge: "ISO 9044 / ASTM E2016",
    h1: "Spécifications des toiles de filtration pour systèmes industriels",
    lead: "Configurez des toiles métalliques en acier inoxydable offrant une ouverture rigoureusement régulière, des pertes de charge maîtrisées et une métallurgie vérifiée pour vos lignes de séparation. Fabrication conforme aux tolérances ISO 9044 et ASTM E2016.",
    relatedCta: "Guide de spécification de commande",
    chip: "Tissages unis et hollandais",
    cardNote: "Rouleaux, bandes refendues, disques découpés et cylindres",
    cardLink: "Formats de livraison"
  },
  pt: {
    categoryBadge: "Especificações de malha de filtração",
    standardBadge: "ISO 9044 / ASTM E2016",
    h1: "Especificações de malha de filtração para sistemas industriais",
    lead: "Configure telas metálicas de aço inoxidável com abertura consistente comprovada, características previsíveis de vazão e metalurgia verificada para linhas críticas de separação. Fabricação estritamente alinhada às normas ISO 9044 e ASTM E2016.",
    relatedCta: "Guia de especificação de pedido",
    chip: "Tramas lisa e holandesa",
    cardNote: "Rolos, bobinas fendidas, discos estampados e cilindros",
    cardLink: "Formatos de fornecimento"
  }
};
interface SpecStripItem {
  label: Record<Lang, string>;
  value: Record<Lang, string>;
}
const specStripData: SpecStripItem[] = [{
  label: {
    en: "Aperture Range",
    zh: "孔径范围",
    es: "Rango de apertura",
    de: "Maschenweitenbereich",
    fr: "Plage d'ouverture",
    pt: "Faixa de abertura"
  },
  value: {
    en: "1 µm – 25 mm",
    zh: "1 µm – 25 mm",
    es: "1 µm – 25 mm",
    de: "1 µm – 25 mm",
    fr: "1 µm – 25 mm",
    pt: "1 µm – 25 mm"
  }
}, {
  label: {
    en: "Mesh Count",
    zh: "目数范围",
    es: "Número de mallas",
    de: "Maschenzahl",
    fr: "Nombre de mailles",
    pt: "Contagem de malha"
  },
  value: {
    en: "1 – 635 Mesh",
    zh: "1 – 635 目/英寸",
    es: "1 – 635 mallas/pulgada",
    de: "1 – 635 Mesh",
    fr: "1 – 635 mailles/pouce",
    pt: "1 – 635 malhas/polegada"
  }
}, {
  label: {
    en: "Wire Diameters",
    zh: "丝径范围",
    es: "Diámetros de alambre",
    de: "Drahtdurchmesser",
    fr: "Diamètres de fil",
    pt: "Diâmetros de arame"
  },
  value: {
    en: "0.020 mm – 2.0 mm",
    zh: "0.020 mm – 2.0 mm",
    es: "0,020 mm – 2,0 mm",
    de: "0,020 mm – 2,0 mm",
    fr: "0,020 mm – 2,0 mm",
    pt: "0,020 mm – 2,0 mm"
  }
}, {
  label: {
    en: "Weave Standards",
    zh: "制造执行标准",
    es: "Normas de fabricación",
    de: "Fertigungsnormen",
    fr: "Normes de fabrication",
    pt: "Normas de fabricação"
  },
  value: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  }
}];
export default function MeshSpecHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="herofiltermeshspec" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (lg:col-span-7) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Exactly two badges: category (bg-card) + standard (bg-muted) */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.standardBadge}</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.h1}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild>
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild>
                <Link to="/WireMeshSpecificationsPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.relatedCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column (lg:col-span-5) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-microscope-style-close-up-photogra-fb39a1.png" alt="Microscopic inspection of stainless steel filter wire mesh weave" loading="eager" fetchPriority="high" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.chip}</span>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.cardNote}</span>
                  </div>
                  <Link to="/SlitCoilsAndStampedFilterDiscsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 inline-flex items-center gap-1 shrink-0">
                    <span>{copy.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specStripData.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{pick(lang, spec.label)}</span>
              <span className="mf-spec-strip__value">{pick(lang, spec.value)}</span>
            </div>)}
        </div>
      </div>
    </section>;
}